import { safeAssert } from '@upright/common/safe-assert';
import { chain, Dictionary, every, flow, isNil, lowerCase, maxBy } from 'lodash';
import { friendlySustainabilityMatterTitle, getMatterById } from '../../input/materiality-assessment/matters';
import {
  CompanyLevelMaterialityResult,
  FinancialItem,
  FinancialMaterialityLevel,
  FinancialMaterialityType,
  GenericLevel,
  MaterialityActualness,
  MaterialityType,
  MaterialityValence,
  MaterialityValueChain,
  SeverityLevel,
} from '../../input/materiality-assessment/types';
import { CsrdMatterHierarchy, SustainabilityMatterId } from '../../input/materiality-assessment/types-matters';
import {
  ProductLevelDirectTriggerResult,
  ProductLevelMaskedResult,
  ProductLevelPriorTriggerResult,
  ProductLevelTriggerResult,
} from '../../input/materiality-assessment/types-product-level-trigger-results';
import { magnitudeToFmLevel, severityLevelToNumber } from '../../input/materiality-assessment/utils';
import { MaterialityResultOfMatter } from './csrd-company-results/types';
import { friendlyShortSeverityLevel } from './friendly-materiality-severity';
import {
  isProductLevelDirectTriggerResult,
  isProductLevelMaskedResult,
  isProductLevelPriorTriggerResult,
} from './trigger-type-guards';
import { plural } from 'pluralize';
import { CsrdImpactTopic, impactTopicKey } from '../../input/materiality-assessment/impact-topics';

export function getMaterialityTopicPath(matterId: SustainabilityMatterId, pathSoFar?: string): string {
  const matter = getMatterById(matterId);
  const currentTopicText = friendlySustainabilityMatterTitle(matterId);

  const pathAndCurrent = pathSoFar ? `${currentTopicText} / ${pathSoFar}` : currentTopicText;

  if (matter.parent) return getMaterialityTopicPath(matter.parent, pathAndCurrent);

  const hierarchy = friendlyMaterialityTopicHierarchy(matter.hierarchy);
  return `${hierarchy} topics / ${pathAndCurrent}`;
}

export function friendlyMaterialityResult(result: CompanyLevelMaterialityResult) {
  switch (result) {
    case CompanyLevelMaterialityResult.MATERIAL:
      return 'Material';
    case CompanyLevelMaterialityResult.NO_TRIGGERS:
      return 'Unknown';
    case CompanyLevelMaterialityResult.NONE_TRIGGERED:
      return 'Not material';
  }
}

function friendlyMaterialityTopicHierarchy(value: CsrdMatterHierarchy) {
  switch (value) {
    case CsrdMatterHierarchy.ESRS:
      return 'ESRS';
    case CsrdMatterHierarchy.UPRIGHT:
      return 'Upright';
  }
}

export function friendlyMaterialityValence(valence: MaterialityValence) {
  return valence === MaterialityValence.NEGATIVE ? 'Negative' : 'Positive';
}

export function friendlyMaterialityValences(valences: MaterialityValence[]) {
  if (valences.length === 2) {
    return 'Both positive and negative';
  }
  if (valences.length === 1) {
    switch (valences[0]) {
      case MaterialityValence.POSITIVE:
        return 'Positive';
      case MaterialityValence.NEGATIVE:
        return 'Negative';
    }
  }
  return '';
}

export function friendlyFinancialMaterialityType(
  types: FinancialMaterialityType[],
): FriendlyFinancialValenceCapitalized | 'Both risks and opportunities' {
  if (types.length === 2) {
    return 'Both risks and opportunities';
  }
  safeAssert(types.length === 1);
  const financialType = types[0];
  switch (financialType) {
    case FinancialMaterialityType.OPPORTUNITY:
      return 'Opportunity';
    case FinancialMaterialityType.RISK:
      return 'Risk';
  }
}

export type FriendlyFinancialValence = 'opportunity' | 'risk';
export type FriendlyFinancialValenceCapitalized = Capitalize<FriendlyFinancialValence>;
export type FriendlyImpactMaterialityType = 'impact';

export type FriendlyIroType = FriendlyFinancialValence | FriendlyImpactMaterialityType;

export function friendlyFinancialValence<
  T extends { lowerCase?: boolean; pluralize?: boolean } = Record<string, never>,
>(
  valence: MaterialityValence,
  options: T = {} as T,
): T extends { lowerCase: true } ? FriendlyFinancialValence : FriendlyFinancialValenceCapitalized {
  const baseResult: FriendlyFinancialValenceCapitalized =
    valence === MaterialityValence.POSITIVE ? 'Opportunity' : 'Risk';

  return flow(
    (text: string) => (options.lowerCase ? lowerCase(text) : text),
    (text: string) => (options.pluralize ? plural(text) : text),
  )(baseResult) as T extends { lowerCase: true } ? FriendlyFinancialValence : FriendlyFinancialValenceCapitalized;
}

export function financeLevelToSeverityLevel(level: FinancialMaterialityLevel): SeverityLevel {
  switch (level) {
    case FinancialMaterialityLevel.NONE:
    case FinancialMaterialityLevel.VERY_LOW:
      return SeverityLevel.NONE;
    case FinancialMaterialityLevel.LOW:
      return SeverityLevel.LOW;
    case FinancialMaterialityLevel.MEDIUM:
      return SeverityLevel.MEDIUM;
    case FinancialMaterialityLevel.HIGH:
      return SeverityLevel.HIGH;
  }
}

export function friendlyMaterialityActualnesses(actualnesses: MaterialityActualness[]) {
  if (actualnesses.length === 2) {
    return 'Both actual and potential impacts';
  }
  if (actualnesses.length === 1) {
    if (actualnesses[0] === MaterialityActualness.POTENTIAL) {
      return 'Potential impact';
    } else {
      return 'Actual impact';
    }
  }
  return '';
}

export function friendlyRiskDriverType(driverType: string | undefined) {
  if (!driverType) {
    return '';
  }
  switch (driverType) {
    case 'I':
      return 'Investors';
    case 'C':
      return 'Customers';
    case 'R':
      return 'Regulators';
    case 'E':
      return 'Employees';
    case 'O':
      return 'Other';
    default:
      return driverType;
  }
}

export function friendlyFinancialItem(financialItem: FinancialItem | undefined) {
  if (!financialItem) {
    return '';
  }
  return financialItem;
}

export function friendlyRiskScale(scale: number) {
  const magnitudeLevel = magnitudeToFmLevel(scale);
  return friendlyRiskMagnitude(magnitudeLevel);
}

export function friendlyRiskProbability(level: FinancialMaterialityLevel) {
  return friendlyFourPointLevel(level);
}

export function friendlyOpportunityLevel(level: FinancialMaterialityLevel) {
  return friendlyFourPointLevel(level);
}

export function friendlyRiskMagnitude(level: FinancialMaterialityLevel) {
  return friendlyFourPointLevel(level);
}

export function friendlyFourPointLevel(level: FinancialMaterialityLevel) {
  switch (level) {
    case FinancialMaterialityLevel.HIGH:
      return 'High';
    case FinancialMaterialityLevel.MEDIUM:
      return 'Medium';
    case FinancialMaterialityLevel.LOW:
      return 'Low';
    case FinancialMaterialityLevel.VERY_LOW:
      return 'Very low';
    case FinancialMaterialityLevel.NONE:
      return 'None';
  }
}

export function friendlyFinancialMaterialityLevel(level: FinancialMaterialityLevel) {
  switch (level) {
    case FinancialMaterialityLevel.HIGH:
      return 'High';
    case FinancialMaterialityLevel.MEDIUM:
      return 'Medium';
    case FinancialMaterialityLevel.LOW:
      return 'Low';
    case FinancialMaterialityLevel.VERY_LOW:
      return 'Very low';
    case FinancialMaterialityLevel.NONE:
      return 'None';
  }
}

export function friendlyGenericLevel(level: GenericLevel) {
  switch (level) {
    case GenericLevel.VERY_LOW:
      return `Very low`;
    case GenericLevel.LOW:
      return `Low`;
    case GenericLevel.MEDIUM:
      return `Medium`;
    case GenericLevel.HIGH:
      return `High`;
  }
}

export function friendlyShortMaterialityLevel(
  datum:
    | { materialityType: MaterialityType.IMPACT; severityLevel: SeverityLevel }
    | { materialityType: MaterialityType.FINANCIAL; materialityLevel: FinancialMaterialityLevel },
) {
  switch (datum.materialityType) {
    case MaterialityType.FINANCIAL:
      return friendlyShortFinancialMaterialityLevel(datum.materialityLevel);
    case MaterialityType.IMPACT:
      return friendlyShortSeverityLevel(datum.severityLevel);
  }
}

function friendlyShortFinancialMaterialityLevel(level: FinancialMaterialityLevel) {
  switch (level) {
    case FinancialMaterialityLevel.HIGH:
      return 'High';
    case FinancialMaterialityLevel.MEDIUM:
      return 'Med';
    case FinancialMaterialityLevel.LOW:
      return 'Low';
    case FinancialMaterialityLevel.VERY_LOW:
      return 'Very low';
    case FinancialMaterialityLevel.NONE:
      return 'None';
  }
}

/**
 * Sets either severityLevel or materialityLevel to NONE for non-material matters.
 * This is done because materiality level is distinct from the `isMaterial` assessment in our data model.
 * As such, we don't want to confuse users by displaying levels for a matter, while simultaneously communicating non-materiality.
 */
export function castLevelToNoneIfNotMaterial<
  T extends {
    isMaterial: boolean;
    severityLevel?: SeverityLevel;
    materialityLevel?: FinancialMaterialityLevel;
  },
>(result: T): T {
  if (result.isMaterial) {
    return result;
  }

  return {
    ...result,
    ...(result.severityLevel !== undefined && { severityLevel: SeverityLevel.NONE }),
    ...(result.materialityLevel !== undefined && { materialityLevel: FinancialMaterialityLevel.NONE }),
  };
}

export function friendlyMaterialityStrategyLink(hasTriggeredProducts: boolean) {
  if (hasTriggeredProducts) return `Linked to business model, as inherent to company's products & services`;
  return '';
}

export const friendlyValueChain = (valueChain: MaterialityValueChain): string => {
  switch (valueChain) {
    case MaterialityValueChain.ALL:
      return 'All value chains';
    case MaterialityValueChain.INTERNAL:
      return 'Internal';
    case MaterialityValueChain.UPSTREAM:
      return 'Upstream';
    case MaterialityValueChain.UPSTREAM_AND_INTERNAL:
      return 'Upstream and internal';
    case MaterialityValueChain.DOWNSTREAM:
      return 'Downstream';
    case MaterialityValueChain.DOWNSTREAM_AND_INTERNAL:
      return 'Downstream and internal';
    case MaterialityValueChain.UPSTREAM_AND_DOWNSTREAM:
      return 'Upstream and downstream';
    case MaterialityValueChain.UNKNOWN:
      return 'Unknown';
  }
};

/**
 * @deprecated Delete when MatterMaterialitySummary gets removed
 */
export function maybeGetMaterialityResultOfType(
  materiality: MaterialityResultOfMatter | undefined,
  materialityType: MaterialityType,
) {
  if (!materiality) return undefined;
  return getMaterialityResultOfType(materiality, materialityType);
}

export function getMaterialityResultOfType(materiality: MaterialityResultOfMatter, materialityType: MaterialityType) {
  switch (materialityType) {
    case MaterialityType.IMPACT:
      return materiality.imResult;
    case MaterialityType.FINANCIAL:
      return materiality.fmResult;
  }
}

export function getIsMaterial(materiality: MaterialityResultOfMatter, materialityType: MaterialityType) {
  return getMaterialityResultOfType(materiality, materialityType) === CompanyLevelMaterialityResult.MATERIAL;
}

export function getProductLevelTriggerResultWithMaxSeverity(
  triggerResults: ProductLevelTriggerResult[],
  valence: MaterialityValence | undefined,
) {
  if (!triggerResults.length || valence === undefined) {
    return undefined;
  }
  const resultsWithSeverity = triggerResults
    .filter(productTriggerResultHasSeverity)
    .filter(tr => tr.trigger.materialityValence === valence);
  const maxSeverityResult = maxBy(resultsWithSeverity, result =>
    isProductLevelMaskedResult(result) ? severityLevelToNumber(result.severityLevel) : result.severityScore,
  );
  if (!isNil(maxSeverityResult)) {
    return maxSeverityResult;
  }
  return resultsWithSeverity.length ? resultsWithSeverity[0] : triggerResults[0];
}

// TODO: maybe combine with server-side function hasSeverity and move to common
export function productTriggerResultHasSeverity(
  triggerResult: ProductLevelTriggerResult,
): triggerResult is ProductLevelPriorTriggerResult | ProductLevelDirectTriggerResult | ProductLevelMaskedResult {
  return (
    isProductLevelDirectTriggerResult(triggerResult) ||
    isProductLevelPriorTriggerResult(triggerResult) ||
    isProductLevelMaskedResult(triggerResult)
  );
}

export function getSeverityAttributes(result: ProductLevelTriggerResult | undefined) {
  if (!result || !productTriggerResultHasSeverity(result))
    return {
      scaleLevel: undefined,
      scope: undefined,
      irremediabilityLevel: undefined,
      probabilityLevel: undefined,
      severityScore: undefined,
      severityLevel: undefined,
    };

  if (isProductLevelMaskedResult(result)) {
    return {
      scaleLevel: result.scaleLevel,
      scope: undefined,
      irremediabilityLevel: result.irremediabilityLevel,
      probabilityLevel: result.probabilityLevel,
      severityScore: undefined,
      severityLevel: result.severityLevel,
    };
  }
  return {
    scaleLevel: result.scaleLevel,
    scope: result.scope,
    irremediabilityLevel: result.irremediabilityLevel,
    probabilityLevel: result.probabilityLevel,
    severityScore: result.severityScore,
    severityLevel: result.severityLevel,
  };
}

export function filterEntries<T extends { description: string }>(
  entries: T[],
  bestValueFunc: (entry: T) => number,
): T[] {
  return chain(entries)
    .groupBy(datum => datum.description)
    .mapValues(data => maxBy(data, bestValueFunc))
    .values()
    .compact()
    .value();
}

export function isAnyMaterial<T extends { isMaterial: boolean | undefined }>(data: T[] | undefined): boolean {
  return data ? data.some(datum => datum.isMaterial) : false;
}

export function friendlyMaterialityType(materialityType: MaterialityType) {
  switch (materialityType) {
    case MaterialityType.IMPACT:
      return 'impact';
    case MaterialityType.FINANCIAL:
      return 'financial';
  }
}

/**
 * Cleans and deconstructs an array of MaterialityValueChain values for visualization.
 *
 * Reasoning: The API may return composite values (e.g., ALL, UPSTREAM_AND_DOWNSTREAM).
 * For visualization, we must break these down into their fundamental, non-overlapping parts:
 * UPSTREAM, INTERNAL, DOWNSTREAM and UNKNOWN (only when given a single UNKNOWN value chain).
 *
 * This function ensures the output only contains these atomic values, in the correct
 * display order.
 *
 * @example
 * // Returns [UPSTREAM, DOWNSTREAM]
 * cleanseValueChain([MaterialityValueChain.UPSTREAM_AND_DOWNSTREAM]);
 *
 * @example
 * // Returns [UPSTREAM, INTERNAL, DOWNSTREAM]
 * cleanseValueChain([MaterialityValueChain.ALL, MaterialityValueChain.UPSTREAM]);
 */
export function cleanseValueChain(value: MaterialityValueChain | MaterialityValueChain[]): MaterialityValueChain[] {
  const atomicValues = new Set<MaterialityValueChain>();
  const inputValues = Array.isArray(value) ? value : [value];

  if (every(inputValues, v => v === MaterialityValueChain.UNKNOWN)) {
    return [MaterialityValueChain.UNKNOWN];
  }

  // Go through each value from the API and add its atomic parts to our set.
  for (const val of inputValues) {
    switch (val) {
      case MaterialityValueChain.ALL:
        atomicValues.add(MaterialityValueChain.UPSTREAM);
        atomicValues.add(MaterialityValueChain.INTERNAL);
        atomicValues.add(MaterialityValueChain.DOWNSTREAM);
        break;
      case MaterialityValueChain.UPSTREAM_AND_DOWNSTREAM:
        atomicValues.add(MaterialityValueChain.UPSTREAM);
        atomicValues.add(MaterialityValueChain.DOWNSTREAM);
        break;
      case MaterialityValueChain.UPSTREAM_AND_INTERNAL:
        atomicValues.add(MaterialityValueChain.UPSTREAM);
        atomicValues.add(MaterialityValueChain.INTERNAL);
        break;
      case MaterialityValueChain.DOWNSTREAM_AND_INTERNAL:
        atomicValues.add(MaterialityValueChain.DOWNSTREAM);
        atomicValues.add(MaterialityValueChain.INTERNAL);
        break;
      case MaterialityValueChain.UNKNOWN:
        atomicValues.add(MaterialityValueChain.UNKNOWN);
        break;
      // These are already atomic, so just add them.
      case MaterialityValueChain.UPSTREAM:
      case MaterialityValueChain.INTERNAL:
      case MaterialityValueChain.DOWNSTREAM:
        atomicValues.add(val);
        break;
    }
  }

  const displayOrder = [
    MaterialityValueChain.UPSTREAM,
    MaterialityValueChain.INTERNAL,
    MaterialityValueChain.DOWNSTREAM,
  ];

  return displayOrder.filter(item => atomicValues.has(item));
}

export function mergeValueChains(values: MaterialityValueChain[]): MaterialityValueChain {
  // First, resolve all input to their atomic components using cleanseValueChain
  // (so we can support both composite and atomic "values" in input)
  const allAtomic = new Set<MaterialityValueChain>(values.flatMap(val => cleanseValueChain(val)));

  // Helper: Return a function that checks if the set covers a list of required atomics
  const has = (required: MaterialityValueChain[]) =>
    required.every(v => allAtomic.has(v)) && allAtomic.size === required.length;

  // Compose largest possible, smallest covering set in priority order
  if (has([MaterialityValueChain.UPSTREAM, MaterialityValueChain.INTERNAL, MaterialityValueChain.DOWNSTREAM])) {
    return MaterialityValueChain.ALL;
  }

  if (has([MaterialityValueChain.UPSTREAM, MaterialityValueChain.INTERNAL])) {
    return MaterialityValueChain.UPSTREAM_AND_INTERNAL;
  }

  if (has([MaterialityValueChain.DOWNSTREAM, MaterialityValueChain.INTERNAL])) {
    return MaterialityValueChain.DOWNSTREAM_AND_INTERNAL;
  }

  if (has([MaterialityValueChain.UPSTREAM, MaterialityValueChain.DOWNSTREAM])) {
    return MaterialityValueChain.UPSTREAM_AND_DOWNSTREAM;
  }

  if (allAtomic.has(MaterialityValueChain.UNKNOWN)) {
    return MaterialityValueChain.UNKNOWN;
  }

  // If only one value (after atomic decomposition), return it directly
  if (allAtomic.size === 1) {
    return Array.from(allAtomic)[0];
  }

  throw new Error(`Encountered an unexpected set of value chains ${Array.from(allAtomic).join(', ')}`);
}

export function friendlyActuality(level: MaterialityActualness): 'Actual' | 'Potential' {
  switch (level) {
    case MaterialityActualness.ACTUAL:
      return 'Actual';
    case MaterialityActualness.POTENTIAL:
      return 'Potential';
    default:
      throw new Error(`Unknown MaterialityActualness level: ${level as string}`);
  }
}

/**
 * Thrown by `getInformationFromImpactTopic` when the trigger's
 * `impactTopicReference` cannot be resolved against the live impact-topic
 * hierarchy — i.e. an *expected* "bad data" condition such as a retired or
 * renamed topic, or a sheet row whose matter/valence no longer match the
 * referenced topic. Callers may safely catch this class to skip the row;
 * any other `Error` from `getInformationFromImpactTopic` (e.g. a
 * `safeAssert` invariant failure) is a genuine bug and should propagate.
 */
export class ImpactTopicNotFoundError extends Error {
  readonly matterId: SustainabilityMatterId;
  readonly materialityValence: MaterialityValence;
  readonly externalDescription: string;
  readonly impactTopicReference: {
    matterId: SustainabilityMatterId;
    valence: MaterialityValence;
    externalDescription: string;
  };

  constructor(
    message: string,
    context: {
      datum: { matterId: SustainabilityMatterId; materialityValence: MaterialityValence; externalDescription: string };
      impactTopicReference: {
        matterId: SustainabilityMatterId;
        valence: MaterialityValence;
        externalDescription: string;
      };
    },
  ) {
    super(message);
    this.name = 'ImpactTopicNotFoundError';
    this.matterId = context.datum.matterId;
    this.materialityValence = context.datum.materialityValence;
    this.externalDescription = context.datum.externalDescription;
    this.impactTopicReference = context.impactTopicReference;
  }
}

export function getInformationFromImpactTopic(
  datum: { matterId: SustainabilityMatterId; materialityValence: MaterialityValence; externalDescription: string },
  impactTopicReference:
    | { matterId: SustainabilityMatterId; valence: MaterialityValence; externalDescription: string }
    | undefined,
  impactTopicHierarchy: Dictionary<CsrdImpactTopic[]>,
) {
  if (!impactTopicReference) {
    return undefined;
  }
  if (impactTopicReference.matterId !== datum.matterId || impactTopicReference.valence !== datum.materialityValence) {
    throw new ImpactTopicNotFoundError(
      `Impact topic ${impactTopicReference.externalDescription} not matching matter ${impactTopicReference.matterId} and valence ${impactTopicReference.valence} `,
      { datum, impactTopicReference },
    );
  }
  const reasoning = datum.externalDescription;
  const externalDescription = impactTopicReference.externalDescription;
  const topicKey = impactTopicKey(impactTopicReference);
  const impactTopics = impactTopicHierarchy[topicKey];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!impactTopics) {
    throw new ImpactTopicNotFoundError(`Impact topic ${topicKey} not found`, { datum, impactTopicReference });
  }
  // `safeAssert` throws a plain `Error` (not `ImpactTopicNotFoundError`) — this
  // is a programming invariant violation (more than one topic for the same
  // matter+valence+description key), not "expected bad data", and must
  // propagate past callers that only catch `ImpactTopicNotFoundError`.
  safeAssert(impactTopics.length === 1, `Only expecting one topic for ${topicKey}`);
  const impactTopicId = impactTopics[0].topicId;
  return { reasoning, externalDescription, impactTopicId };
}
